import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Checkbox, Chip, Container, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, InputAdornment, FormControlLabel, Switch,
} from '@mui/material';
import axios from 'axios';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import { matchesSearch } from '../utils/search';
import { formatPhoneDisplay } from '../utils/phone';
import { FUNCTIONS_BASE, PAYMENT_KIND, PAYMENT_METHOD, fmtDateTime, formatPrice, layawayApi, toInputDate } from '../utils/layaways';

// Pagos de layaway (pedido de Tuto 2026-10-05): TODOS los depósitos, abonos y
// almacenajes que entran (desde la app/web o en tienda) en una sola lista,
// para pasarlos al POS de la tienda (Celerant) y marcar cuáles ya se entraron.
const RANGES = [
  { key: 'today', label: 'Hoy', days: 0 },
  { key: '7d', label: '7 días', days: 7 },
  { key: '30d', label: '30 días', days: 30 },
  { key: 'all', label: 'Todo', days: null },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

export default function LayawayPaymentsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ pendingCount: 0, pendingTotalCents: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [range, setRange] = useState('7d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyPending, setOnlyPending] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [busyId, setBusyId] = useState(null);

  const bounds = useMemo(() => {
    if (range === 'custom') {
      return { from: from ? startOfDay(new Date(`${from}T12:00:00`)) : null, to: to ? endOfDay(new Date(`${to}T12:00:00`)) : null };
    }
    const r = RANGES.find((x) => x.key === range);
    if (!r || r.days === null) return { from: null, to: null };
    const f = startOfDay(new Date()); f.setDate(f.getDate() - r.days);
    return { from: f, to: null };
  }, [range, from, to]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${FUNCTIONS_BASE}/adminListLayawayPayments`, {
        from: bounds.from ? bounds.from.toISOString() : null,
        to: bounds.to ? bounds.to.toISOString() : null,
        onlyPending: false,
      });
      setRows(r.data.payments || []);
      setMeta({ pendingCount: r.data.pendingCount || 0, pendingTotalCents: r.data.pendingTotalCents || 0 });
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { setLoading(false); }
  }, [bounds]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((p) => {
    if (onlyPending && (p.postedToPos || p.voided)) return false;
    if (!search.trim()) return true;
    return matchesSearch(search, [p.memberName, p.memberPhone, p.firearm, p.serialNumber, p.contractNumber, p.note, p.createdBy]);
  }), [rows, onlyPending, search]);

  const totals = useMemo(() => {
    const live = filtered.filter((p) => !p.voided);
    return {
      count: live.length,
      total: live.reduce((s, p) => s + p.totalCents, 0),
      cash: live.filter((p) => p.method === 'cash').reduce((s, p) => s + p.totalCents, 0),
      online: live.filter((p) => p.method === 'card_online').reduce((s, p) => s + p.totalCents, 0),
    };
  }, [filtered]);

  const togglePosted = async (p, posted) => {
    setBusyId(p.id);
    try {
      await layawayApi.update(p.layawayId, 'markPosted', { paymentId: p.id, posted });
      setRows((prev) => prev.map((x) => (x.id === p.id ? { ...x, postedToPos: posted, postedAt: posted ? new Date().toISOString() : null } : x)));
      setMeta((m) => ({ ...m, pendingCount: Math.max(0, m.pendingCount + (posted ? -1 : 1)), pendingTotalCents: Math.max(0, m.pendingTotalCents + (posted ? -p.totalCents : p.totalCents)) }));
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  const markAllVisible = async () => {
    const pend = filtered.filter((p) => !p.postedToPos && !p.voided);
    if (!pend.length) return;
    if (!window.confirm(`¿Marcar ${pend.length} pago(s) como entrados en el POS?`)) return;
    // eslint-disable-next-line no-restricted-syntax
    for (const p of pend) {
      // eslint-disable-next-line no-await-in-loop
      await togglePosted(p, true);
    }
  };

  const exportCsv = () => {
    const head = ['Fecha', 'Cliente', 'Teléfono', 'Arma', 'Serie', 'Contrato', 'Concepto', 'Método', 'Monto', 'IVU', 'Total', 'Origen', 'Nota', 'Entrado POS', 'Balance layaway'];
    const lines = filtered.map((p) => [
      fmtDateTime(p.createdAt), p.memberName, formatPhoneDisplay(p.memberPhone), p.firearm, p.serialNumber, p.contractNumber,
      `${PAYMENT_KIND[p.kind] || p.kind}${p.months ? ` ${p.months} mes(es)` : ''}${p.voided ? ' (ANULADO)' : ''}`,
      PAYMENT_METHOD[p.method] || p.method, (p.amountCents / 100).toFixed(2), (p.taxCents / 100).toFixed(2), (p.totalCents / 100).toFixed(2),
      p.createdBy === 'customer' ? 'Cliente (app/web)' : p.createdBy, p.note, p.postedToPos ? 'Sí' : 'No', (p.layawayBalanceCents / 100).toFixed(2),
    ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([` ${[head.join(','), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `pagos-layaway-${toInputDate(new Date())}.csv`; a.click();
  };

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Helmet><title> Pagos de layaway | BBTC </title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
          <div>
            <Typography variant="h4">Pagos de layaway</Typography>
            <Typography variant="body2" color="text.secondary">Todo lo que los clientes van abonando (app, web o tienda). Marca cada pago cuando lo entres en el POS.</Typography>
          </div>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Iconify icon="eva:download-outline" />} onClick={exportCsv} disabled={!filtered.length}>CSV</Button>
            <Button variant="outlined" startIcon={<Iconify icon="eva:refresh-fill" />} onClick={load}>Actualizar</Button>
            <Button variant="contained" startIcon={<Iconify icon="eva:checkmark-square-2-outline" />} onClick={markAllVisible} disabled={!filtered.some((p) => !p.postedToPos && !p.voided)}>Marcar visibles como entrados</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Pendientes de entrar al POS', value: meta.pendingCount, color: meta.pendingCount ? 'warning.main' : 'success.main' },
            { label: 'Monto pendiente', value: formatPrice(meta.pendingTotalCents) },
            { label: 'En la vista', value: `${totals.count} · ${formatPrice(totals.total)}` },
            { label: 'Efectivo / Tarjeta online (vista)', value: `${formatPrice(totals.cash)} / ${formatPrice(totals.online)}` },
          ].map((k) => (
            <Grid item xs={6} md={3} key={k.label}>
              <Card sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography variant="h5" color={k.color}>{k.value}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Card>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ p: 2 }} alignItems={{ lg: 'center' }} flexWrap="wrap">
            <ToggleButtonGroup exclusive size="small" value={range} onChange={(e, v) => { if (v) { setRange(v); setPage(0); } }}>
              {RANGES.map((r) => <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>)}
              <ToggleButton value="custom">Rango</ToggleButton>
            </ToggleButtonGroup>
            {range === 'custom' && (
              <Stack direction="row" spacing={1}>
                <TextField size="small" type="date" label="Desde" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="Hasta" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Stack>
            )}
            <FormControlLabel control={<Switch checked={onlyPending} onChange={(e) => { setOnlyPending(e.target.checked); setPage(0); }} />} label="Solo pendientes de entrar al POS" />
            <TextField
              size="small" placeholder="Buscar cliente, teléfono, arma, serie…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} /></InputAdornment> }}
            />
          </Stack>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"><Tooltip title="Entrado en el POS"><span>POS</span></Tooltip></TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Arma</TableCell>
                    <TableCell>Concepto</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && <TableSkeleton rows={8} columns={10} />}
                  {!loading && pageRows.length === 0 && (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>{onlyPending ? 'No hay pagos pendientes de entrar al POS en este período.' : 'No hay pagos en este período.'}</TableCell></TableRow>
                  )}
                  {!loading && pageRows.map((p) => (
                    <TableRow key={p.id} hover sx={p.voided ? { opacity: 0.5, textDecoration: 'line-through' } : p.postedToPos ? { bgcolor: 'action.hover' } : undefined}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={Boolean(p.postedToPos)} disabled={p.voided || busyId === p.id} onChange={(e) => togglePosted(p, e.target.checked)} color="success" />
                      </TableCell>
                      <TableCell>
                        {fmtDateTime(p.createdAt)}
                        {p.postedToPos && <Typography variant="caption" display="block" color="success.main">POS {p.postedBy ? `· ${p.postedBy}` : ''} {p.postedAt ? fmtDateTime(p.postedAt) : ''}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{p.memberName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatPhoneDisplay(p.memberPhone)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.firearm}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{p.serialNumber}{p.contractNumber ? ` · ${p.contractNumber}` : ''}</Typography>
                      </TableCell>
                      <TableCell>
                        {PAYMENT_KIND[p.kind] || p.kind}{p.months ? ` · ${p.months} mes${p.months > 1 ? 'es' : ''}` : ''}
                        {p.voided && <Chip size="small" color="error" variant="outlined" label="Anulado" sx={{ ml: 0.5 }} />}
                        {p.note && <Typography variant="caption" display="block" color="text.secondary">{p.note}</Typography>}
                      </TableCell>
                      <TableCell>{PAYMENT_METHOD[p.method] || p.method || '—'}</TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2">{formatPrice(p.totalCents)}</Typography>
                        {p.taxCents > 0 && <Typography variant="caption" color="text.secondary">{formatPrice(p.amountCents)} + IVU {formatPrice(p.taxCents)}</Typography>}
                      </TableCell>
                      <TableCell>
                        {p.createdBy === 'customer'
                          ? <Chip size="small" color="info" label="Cliente (app/web)" />
                          : <Typography variant="caption">{p.createdBy}</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={p.layawayBalanceCents > 0 ? 'error.main' : 'success.main'}>{formatPrice(p.layawayBalanceCents)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Ver ficha del layaway"><Button size="small" onClick={() => navigate(`/dashboard/layaways/details?id=${p.layawayId}`)}><Iconify icon="eva:arrow-ios-forward-fill" /></Button></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]} labelRowsPerPage="Por página"
          />
        </Card>
      </Container>
    </>
  );
}
